import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  ASSET_CLASSES,
  AssetClass,
  AssetClassInvestmentType,
  investmentTypesOf,
  isAssetClass,
} from "@api/v1/hooks/performance/assetClasses";

export interface AssetClassFilterValue {
  // Active asset classes in card order; empty means the whole portfolio.
  activeClasses: AssetClass[];
  isFiltered: boolean;
  // The investment types behind the active classes, for requests and lists.
  investmentTypes: AssetClassInvestmentType[];
  // The classes the cards offer, in card order; empty until they have loaded.
  availableClasses: AssetClass[];
  toggle: (assetClass: AssetClass) => void;
  setActiveClasses: (assetClasses: AssetClass[]) => void;
  clear: () => void;
  // Drops active classes the shown cards don't offer (e.g. another depot).
  setAvailableClasses: (assetClasses: AssetClass[]) => void;
}

const noFilter: AssetClassFilterValue = {
  activeClasses: [],
  isFiltered: false,
  investmentTypes: [],
  availableClasses: [],
  toggle: () => {},
  setActiveClasses: () => {},
  clear: () => {},
  setAvailableClasses: () => {},
};

const AssetClassFilterContext = createContext<AssetClassFilterValue>(noFilter);

export const useAssetClassFilter = () => useContext(AssetClassFilterContext);

const inCardOrder = (assetClasses: AssetClass[]) =>
  ASSET_CLASSES.filter((assetClass) => assetClasses.includes(assetClass));

// A broken or outdated entry falls back to no filter instead of failing.
const readStoredClasses = (storageKey?: string): AssetClass[] => {
  if (!storageKey) return [];
  try {
    const parsed: unknown = JSON.parse(
      localStorage.getItem(storageKey) ?? "[]"
    );
    return Array.isArray(parsed)
      ? inCardOrder(parsed.filter(isAssetClass))
      : [];
  } catch {
    return [];
  }
};

export const AssetClassFilterProvider = ({
  children,
  storageKey,
}: {
  children: ReactNode;
  // Remembers the filter per browser under this key.
  storageKey?: string;
}) => {
  const [activeClasses, setActive] = useState<AssetClass[]>(() =>
    readStoredClasses(storageKey)
  );
  const [availableClasses, setAvailable] = useState<AssetClass[]>([]);

  const toggle = useCallback((assetClass: AssetClass) => {
    setActive((prev) =>
      prev.includes(assetClass)
        ? prev.filter((active) => active !== assetClass)
        : inCardOrder([...prev, assetClass])
    );
  }, []);

  const setActiveClasses = useCallback(
    (assetClasses: AssetClass[]) => setActive(inCardOrder(assetClasses)),
    []
  );

  const clear = useCallback(() => setActive([]), []);

  // Callers pass a fresh array on every render (the cards' data isn't stable);
  // keeping the previous state when nothing changed stops that re-rendering.
  const setAvailableClasses = useCallback((available: AssetClass[]) => {
    setAvailable((prev) => {
      const next = inCardOrder(available);
      const isSame =
        next.length === prev.length &&
        next.every((assetClass, index) => assetClass === prev[index]);
      return isSame ? prev : next;
    });
    setActive((prev) => {
      const next = prev.filter((active) => available.includes(active));
      return next.length === prev.length ? prev : next;
    });
  }, []);

  useEffect(() => {
    if (!storageKey) return;
    try {
      localStorage.setItem(storageKey, JSON.stringify(activeClasses));
    } catch {
      // Storage can be unavailable (private mode, quota); the filter still works.
    }
  }, [storageKey, activeClasses]);

  const value = useMemo<AssetClassFilterValue>(
    () => ({
      activeClasses,
      isFiltered: activeClasses.length > 0,
      investmentTypes: investmentTypesOf(activeClasses),
      availableClasses,
      toggle,
      setActiveClasses,
      clear,
      setAvailableClasses,
    }),
    [
      activeClasses,
      availableClasses,
      toggle,
      setActiveClasses,
      clear,
      setAvailableClasses,
    ]
  );

  return (
    <AssetClassFilterContext.Provider value={value}>
      {children}
    </AssetClassFilterContext.Provider>
  );
};
